import { AxiosErrorResponse } from "@/@types/Errors";
import { CreateIntegrationRequest } from "@/@types/Integrations";
import { PaymentMethodAvailable } from "@/@types/User";
import { useAuth } from "@/auth/Auth";
import ActiveCampaignListSelect from "@/components/Integrations/ActiveCampaignListSelect";
import { Input } from "@/components/shared/Input/Input";
import PaymentMethodSelector from "@/components/shared/PaymentMethodSelector/PaymentMethodSelector";
import StatusSelector from "@/components/shared/StatusSelector/StatusSelector";
import IntegrationTestModal, {
  getMissingFieldsForTest,
  integrationTestConfigs,
  useCanTestIntegration,
} from "@/components/shared/IntegrationTestModal/IntegrationTestModal";
import { Label as SharedLabel } from "@/components/shared/Label/Label";
import MultiSelect from "@/components/shared/MultipleSelect/MultipleSelect";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { SheetFooter, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { toast } from "@/hooks/use-toast";
import { cn } from "@/lib/utils";
import { IntegrationsService } from "@/services/integrations.service";
import { ProductsService } from "@/services/products.service";
import useModalStore from "@/store/useModalStore";
import { copyToClipboard } from "@/utils/copyToClipboard";
import {
  invalidateWmsProductOptions,
  isWmsIntegration,
  productCategoryTypesForIntegration,
} from "@/utils/integrationProductFilters";
import { paymentStatus } from "@/utils/paymentMethods";
import { zodResolver } from "@hookform/resolvers/zod";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { AxiosError } from "axios";
import { motion } from "framer-motion";
import { Check, Copy, Loader2, Zap } from "lucide-react";
import { ReactNode, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useDebounce } from "use-debounce";
import { AnyZodObject, z } from "zod";
import {
  FieldLabels,
  FixedOptions,
  FixedValues,
} from "../IntegrationTable/IntegrationTable";
import { MultiComboBox } from "../MultiComboBox/MultiComboBox";
import { Notice } from "../Notice";

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.06,
      delayChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 15 },
  visible: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.35, ease: "easeOut" },
  },
};

const checkboxContainerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.03,
      delayChildren: 0.05,
    },
  },
};

const checkboxItemVariants = {
  hidden: { opacity: 0, x: -10 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.2, ease: "easeOut" },
  },
};

type Props = {
  integrationKey: string;
  modalKey: string;
  fields: ("access_token" | "postback_url" | "metadata" | "secret_token")[];
  additionalSchema?: AnyZodObject;
  icon: ReactNode;
  title: string;
  fixedOptions?: FixedOptions;
  fixedValues?: FixedValues;
  fieldLabels?: FieldLabels;
};

export default function GenericCreateIntegration({
  integrationKey,
  modalKey,
  fields,
  additionalSchema,
  icon,
  title,
  fixedOptions = {},
  fixedValues,
  fieldLabels,
}: Props) {
  const { t } = useTranslation(["common", "integration"]);
  const { closeModal } = useModalStore();
  const { authData } = useAuth();
  const [copied, setCopied] = useState(false);
  const [isTestModalOpen, setIsTestModalOpen] = useState(false);

  const schemaBase = z.object({
    title: z.string().min(3, t("required_field")),
    products: z.array(z.string()).min(1, t("required_products")),
    payment_methods: z.array(z.string()).min(1, t("required_payment_methods")),
    active_statuses: z.array(z.string()).min(1, t("required_active_statuses")),
    ...(fields.includes("access_token") && {
      access_token: z.string().min(1, t("required_field")),
    }),
    ...(fields.includes("secret_token") && {
      secret_token: z.string().min(1, t("required_field")),
    }),
    ...(fields.includes("postback_url") && {
      postback_url: z
        .string()
        .url(t("invalid_url"))
        .min(1, t("required_field"))
        .refine(
          (value) => integrationKey !== "cademi" || (
            value.startsWith("https://") &&
            !/\/entrega\/enviar\/?(?:[?#].*)?$/i.test(value)
          ),
          t("invalid_url")
        ),
    }),
    ...(fields.includes("metadata") && {
      metadata:
        integrationKey === "active_campaign"
          ? z.object({
            action: z.enum(["insert", "remove"]),
            list: z.number().min(1, t("required_field")),
            apply_tags: z.boolean().default(false),
            initiate_checkout_trigger: z
              .enum(["checkout_open", "email_fill", "all_contact_data"])
              .default("email_fill"),
          })
          : integrationKey === "enotas"
            ? z.object({
              empresa_id: z.string().min(1, t("required_field")),
              enotas_note_type: z.enum(["nfse", "nfe"], {
                required_error: t("required_field"),
              }),
            })
            : integrationKey === "notazz"
              ? z.object({
                notazz_note_type: z.enum(["nfse", "nfe"], {
                  required_error: t("required_field"),
                }),
              })
              : integrationKey === "ship_offers"
                ? z.object({
                  store_id: z.string().min(1, t("required_field")),
                })
                : z.array(z.number()).min(1, t("required_field")),
    }),
    ...(!fields.includes("metadata") && integrationKey === "webhook" && {
      metadata: z.record(z.unknown()).optional(),
    }),
  });

  const CreateSchema = additionalSchema
    ? schemaBase.merge(additionalSchema)
    : schemaBase;

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    getValues,
    watch,
    clearErrors,
  } = useForm<CreateIntegrationRequest>({
    shouldUnregister: false,
    resolver: zodResolver(CreateSchema),
    defaultValues: {
      payment_methods:
        integrationKey !== "active_campaign"
          ? fixedValues?.payment_methods ||
          fixedOptions?.payment_methods ||
          authData?.available_payment_methods?.map(m => m.type) ||
          []
          : [],
      active_statuses:
        integrationKey !== "active_campaign"
          ? fixedValues?.active_statuses ||
          fixedOptions?.active_statuses ||
          paymentStatus
          : [],
      metadata:
        fixedValues?.metadata ||
        (integrationKey === "webhook"
          ? { initiate_checkout_trigger: 'email_fill' }
          : integrationKey === "active_campaign"
            ? {
              action: "insert",
              list: 0,
              apply_tags: false,
            }
            : integrationKey === "enotas"
              ? { empresa_id: "", enotas_note_type: "nfse" as const }
              : integrationKey === "notazz"
                ? { notazz_note_type: "nfse" as const }
                : integrationKey === "ship_offers"
                  ? { store_id: "" }
                  : []),
      products: fixedValues?.products || [],
    },
  });

  const defaultValues = watch();
  const queryClient = useQueryClient();

  const { mutate, isPending } = useMutation({
    mutationFn: (data: CreateIntegrationRequest) =>
      IntegrationsService.createIntegration({
        integration: integrationKey,
        data,
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: [integrationKey] });
      if (isWmsIntegration(integrationKey)) {
        await invalidateWmsProductOptions(queryClient);
      }
      toast({
        variant: "success",
        description: t("integration:success_submit"),
      });
      closeModal(modalKey);
    },
    onError: (error: AxiosError<AxiosErrorResponse>) => {
      toast({
        variant: "error",
        description:
          error?.response?.data?.message || t("data_submission_error"),
      });
    },
  });

  const accessToken = watch("access_token");
  const postbackUrl = watch("postback_url");
  const [debouncedToken] = useDebounce(accessToken, 500);
  const [debouncedPostbackUrl] = useDebounce(postbackUrl, 500);

  const {
    data: coursesData,
    isFetching: isFetchingCourses,
    refetch: refetchCourses,
    error: coursesError,
    isError: hasCoursesError,
  } = useQuery<any, Error>({
    queryKey: ["memberkit-courses"],
    queryFn: async () => {
      if (integrationKey !== "memberkit" || !debouncedToken) return [];
      const response = await IntegrationsService.getCoursesMemberkit(
        debouncedToken
      );
      return response?.data;
    },
    enabled: integrationKey === "memberkit" && !!debouncedToken,
    retry: false,
  });

  const {
    data: activeCampaignLists,
    dataUpdatedAt: listsUpdatedAt,
    isFetching: isFetchingLists,
    error: listsError,
    isError: hasListsError,
  } = useQuery<any, Error>({
    queryKey: ["active-campaign-lists", debouncedToken, debouncedPostbackUrl, ""],
    queryFn: async () => {
      if (
        integrationKey !== "active_campaign" ||
        !debouncedToken ||
        !debouncedPostbackUrl
      )
        return [];
      const response = await IntegrationsService.getActiveCampaignLists({
        access_token: debouncedToken,
        postback_url: debouncedPostbackUrl,
      });
      return response?.data;
    },
    enabled:
      integrationKey === "active_campaign" &&
      !!debouncedToken &&
      !!debouncedPostbackUrl,
    retry: false,
  });

  const onSubmit = (data: CreateIntegrationRequest) => {
    // Limpar trigger de IC se initiate_checkout não está nos active_statuses
    if (
      !data.active_statuses?.includes('initiate_checkout') &&
      typeof data.metadata === 'object' &&
      data.metadata !== null &&
      !Array.isArray(data.metadata)
    ) {
      const meta = { ...(data.metadata as Record<string, unknown>) };
      delete meta.initiate_checkout_trigger;
      data.metadata = Object.keys(meta).length > 0 ? meta : undefined;
    }

    mutate({
      ...data,
      status: true,
    });
  };

  useEffect(() => {
    if (hasCoursesError) {
      setValue("metadata", []);
    }
  }, [hasCoursesError]);

  useEffect(() => {
    if (!hasListsError) {
      return;
    }

    const currentMetadata = getValues("metadata");

    if (
      typeof currentMetadata === "object" &&
      currentMetadata !== null &&
      !Array.isArray(currentMetadata)
    ) {
      setValue("metadata", {
        ...currentMetadata,
        list: 0,
      });

      return;
    }

    setValue("metadata", {
      action: "insert",
      list: 0,
      apply_tags: false,
    });
  }, [getValues, hasListsError, setValue]);

  useEffect(() => {
    if (integrationKey === "memberkit" && debouncedToken) {
      refetchCourses();
    }
  }, [debouncedToken, integrationKey]);

  const availableCourses = !hasCoursesError ? coursesData ?? [] : [];
  const availableLists = !hasListsError ? activeCampaignLists ?? [] : [];

  const productCategoryTypes =
    productCategoryTypesForIntegration(integrationKey);
  const wmsProductFetchParams = isWmsIntegration(integrationKey)
    ? [integrationKey]
    : productCategoryTypes;

  const fetchOptions = async (query?: string) => {
    return await ProductsService.getAllProductsBasicOptions(
      query,
      undefined,
      productCategoryTypes,
      isWmsIntegration(integrationKey)
        ? { forIntegration: integrationKey }
        : undefined,
    );
  };

  const handleCopyUserId = async () => {
    if (!authData?.id) return;

    try {
      await copyToClipboard(authData.id);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (error) {
      setCopied(false);
    }
  };

  const formValuesForTest = {
    title: watch("title"),
    postback_url: watch("postback_url"),
    products: watch("products"),
    access_token: watch("access_token"),
  };

  const canTestIntegration = useCanTestIntegration(integrationKey, formValuesForTest);
  const missingFieldsForTest = getMissingFieldsForTest(integrationKey, formValuesForTest);
  const hasTestSupport = Boolean(integrationTestConfigs[integrationKey]);

  const availableStatusesForTest =
    fixedOptions?.active_statuses || paymentStatus;
  const availablePaymentMethodsForTest =
    fixedOptions?.payment_methods ||
    authData?.available_payment_methods?.map(m => m.type) ||
    [];

  const resolvedAvailableMethods = useMemo((): PaymentMethodAvailable[] => {
    const allMethods = authData?.available_payment_methods ?? [];
    if (fixedOptions?.payment_methods) {
      const allowed = new Set(fixedOptions.payment_methods);
      return allMethods.filter(m => allowed.has(m.type));
    }
    return allMethods;
  }, [authData?.available_payment_methods, fixedOptions?.payment_methods]);

  const renderMetadataField = () => {
    if (integrationKey === "notazz" && fields.includes("metadata")) {
      const currentMetadata = (watch("metadata") as { notazz_note_type?: "nfse" | "nfe" }) || {};
      return (
        <div className="space-y-2">
          <Label>{fieldLabels?.metadata || t("integration:notazz_note_type_label")}</Label>
          <Select
            value={currentMetadata.notazz_note_type ?? ""}
            onValueChange={(value: "nfse" | "nfe") => {
              setValue("metadata", { ...currentMetadata, notazz_note_type: value });
              clearErrors("metadata");
            }}
          >
            <SelectTrigger data-cy="select-notazz-note-type">
              <SelectValue placeholder={t("integration:notazz_note_type_placeholder")} />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="nfse">{t("integration:notazz_nfse")}</SelectItem>
              <SelectItem value="nfe">{t("integration:notazz_nfe")}</SelectItem>
            </SelectContent>
          </Select>
          {errors.metadata &&
            typeof errors.metadata === "object" &&
            "notazz_note_type" in errors.metadata && (
              <p className="text-sm text-error">
                {(errors.metadata as { notazz_note_type?: { message?: string } }).notazz_note_type?.message}
              </p>
            )}
        </div>
      );
    }

    if (integrationKey === "memberkit" && fields.includes("metadata")) {
      return (
        <MultiSelect
          disabled={
            !debouncedToken ||
            isFetchingCourses ||
            availableCourses?.length === 0 ||
            hasCoursesError
          }
          options={
            availableCourses?.map((course: any) => ({
              label: course.name,
              value: course.id,
            })) ?? []
          }
          label={fieldLabels?.metadata || t("memberkit.label_courses")}
          placeholder={
            !accessToken
              ? t("memberkit.placeholder_enter_token")
              : isFetchingCourses
                ? t("memberkit.placeholder_loading_courses")
                : t("memberkit.placeholder_select_courses")
          }
          values={watch("metadata") as string[]}
          onValuesChange={(value) => {
            setValue("metadata", value);
            clearErrors("metadata");
          }}
          helperText={errors.metadata?.message as string}
        />
      );
    }

    if (integrationKey === "enotas" && fields.includes("metadata")) {
      const currentMetadata =
        (watch("metadata") as {
          empresa_id?: string;
          enotas_note_type?: "nfse" | "nfe";
        }) || {};
      return (
        <div className="space-y-4">
          <Input
            value={currentMetadata.empresa_id ?? ""}
            label={fieldLabels?.metadata || t("integration:enotas_empresa_id")}
            placeholder={
              fieldLabels?.placeholder_metadata ||
              t("integration:enotas_empresa_id_placeholder")
            }
            data-cy="input-enotas-empresa_id"
            helperText={
              errors.metadata &&
                typeof errors.metadata === "object" &&
                "empresa_id" in errors.metadata
                ? (errors.metadata as { empresa_id?: { message?: string } })
                  .empresa_id?.message
                : undefined
            }
            onChange={(e) => {
              setValue("metadata", {
                ...currentMetadata,
                empresa_id: e.target.value,
              });
              clearErrors("metadata");
            }}
          />

          <div className="space-y-2">
            <Label>
              {fieldLabels?.enotas_note_type ||
                t("integration:notazz_note_type_label")}
            </Label>
            <Select
              value={currentMetadata.enotas_note_type ?? ""}
              onValueChange={(value: "nfse" | "nfe") => {
                setValue("metadata", {
                  ...currentMetadata,
                  enotas_note_type: value,
                });
                clearErrors("metadata");
              }}
            >
              <SelectTrigger data-cy="select-enotas-note-type">
                <SelectValue
                  placeholder={t("integration:notazz_note_type_placeholder")}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="nfse">{t("integration:notazz_nfse")}</SelectItem>
                <SelectItem value="nfe">{t("integration:notazz_nfe")}</SelectItem>
              </SelectContent>
            </Select>
            {errors.metadata &&
              typeof errors.metadata === "object" &&
              "enotas_note_type" in errors.metadata && (
                <p className="text-sm text-error">
                  {(errors.metadata as {
                    enotas_note_type?: { message?: string };
                  }).enotas_note_type?.message}
                </p>
              )}
          </div>
        </div>
      );
    }

    if (integrationKey === "ship_offers" && fields.includes("metadata")) {
      const currentMetadata = (watch("metadata") as { store_id?: string }) || {};
      return (
        <div className="space-y-2">
          <Input
            value={currentMetadata.store_id ?? ""}
            label={fieldLabels?.metadata || t("integration:ship_offers_store_id")}
            placeholder={
              fieldLabels?.placeholder_metadata ||
              t("integration:ship_offers_store_id_placeholder")
            }
            data-cy="input-ship-offers-store-id"
            helperText={
              errors.metadata &&
                typeof errors.metadata === "object" &&
                "store_id" in errors.metadata
                ? (errors.metadata as { store_id?: { message?: string } })
                  .store_id?.message
                : undefined
            }
            onChange={(e) => {
              setValue("metadata", {
                ...currentMetadata,
                store_id: e.target.value,
              });
              clearErrors("metadata");
            }}
          />
        </div>
      );
    }

    if (integrationKey === "active_campaign" && fields.includes("metadata")) {
      const currentMetadata = watch("metadata") as {
        action: string;
        list: number;
        apply_tags: boolean;
      };

      return (
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium mb-2 block">
              {t("integration:active_campaign.action")}
            </label>
            <Select
              value={currentMetadata?.action || "insert"}
              onValueChange={(value) => {
                setValue("metadata", {
                  ...currentMetadata,
                  action: value as "insert" | "remove",
                });
                clearErrors("metadata");
              }}
            >
              <SelectTrigger>
                <SelectValue
                  placeholder={t("integration:active_campaign.select_action")}
                />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="insert">
                  {t("integration:active_campaign.insert")}
                </SelectItem>
                <SelectItem value="remove">
                  {t("integration:active_campaign.remove")}
                </SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm font-medium mb-2 block">
              {t("integration:active_campaign.lists")}
            </label>
            <ActiveCampaignListSelect
              key={listsUpdatedAt}
              accessToken={debouncedToken}
              postbackUrl={debouncedPostbackUrl}
              initialLists={availableLists}
              initialListsUpdatedAt={listsUpdatedAt}
              disabled={!debouncedToken || !debouncedPostbackUrl || isFetchingLists || hasListsError}
              placeholder={
                !accessToken || !postbackUrl
                  ? t("integration:active_campaign.placeholder_enter_credentials")
                  : isFetchingLists
                    ? t("integration:active_campaign.placeholder_loading_lists")
                    : t("integration:active_campaign.placeholder_select_list")
              }
              value={currentMetadata?.list || 0}
              onChange={(list) => {
                setValue("metadata", {
                  ...currentMetadata,
                  list,
                });
                clearErrors("metadata");
              }}
            />
            {"metadata" in errors &&
              typeof errors.metadata === "object" &&
              errors.metadata !== null &&
              "list" in errors.metadata &&
              (errors.metadata as any).list?.message && (
                <p className="text-sm text-error mt-1">
                  {(errors.metadata as any).list.message}
                </p>
              )}
          </div>

          <div className="flex items-start justify-between gap-4">
            <div className="flex flex-col gap-1">
              <SharedLabel htmlFor="active-campaign-apply-tags-create">
                {t("integration:active_campaign.apply_tags_label")}
              </SharedLabel>
              <p className="text-sm text-muted-foreground">
                {t("integration:active_campaign.apply_tags_description")}
              </p>
            </div>

            <Switch
              id="active-campaign-apply-tags-create"
              checked={currentMetadata?.apply_tags ?? false}
              onCheckedChange={(checked) => {
                clearErrors("metadata");
                setValue(
                  "metadata",
                  {
                    ...currentMetadata,
                    apply_tags: checked,
                  },
                  {
                    shouldDirty: true,
                    shouldValidate: true,
                  },
                );
              }}
            />
          </div>

          {currentMetadata?.apply_tags && (
            <Notice
              message={t("integration:active_campaign.apply_tags_notice")}
            />
          )}
        </div>
      );
    }

    return null;
  };

  return (
    <section className="h-full flex flex-col justify-between">
      <SheetHeader className="md:p-6 p-4">
        <motion.div
          initial={{ opacity: 0, y: -10 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.3 }}
          className="space-y-3"
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            transition={{ duration: 0.3, delay: 0.1 }}
          >
            {icon}
          </motion.div>
          <SheetTitle className="text-lg font-semibold text-left">
            {t("create_integration", {
              integration: title,
            })}
          </SheetTitle>
          {integrationKey === "tb3" && authData?.id && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.2 }}
              className="flex items-center gap-2 mt-2"
            >
              <span className="text-sm text-muted-foreground">
                ID: {authData.id}
              </span>
              <Button
                variant="ghost"
                size="icon"
                onClick={handleCopyUserId}
              >
                {copied ? (
                  <Check className="h-4 w-4 text-green-500" />
                ) : (
                  <Copy className="h-4 w-4 text-muted-foreground hover:text-foreground" />
                )}
              </Button>
            </motion.div>
          )}
        </motion.div>
      </SheetHeader>

      <form
        onSubmit={handleSubmit(onSubmit)}
        className="flex flex-col justify-between h-full"
      >
        <motion.section
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className="flex flex-col gap-4 px-4 md:px-6"
        >
          <motion.div variants={itemVariants}>
            <Input
              {...register("title")}
              label={fieldLabels?.title || t("title")}
              placeholder={
                fieldLabels?.placeholder_title || t("title_placeholder")
              }
              data-cy="input-title"
              helperText={errors.title?.message}
            />
          </motion.div>

          {fields.includes("access_token") && (
            <motion.div variants={itemVariants} className="space-y-3">
              {integrationKey === "tauk" && (
                <Notice
                  message={t("integration:tauk_alert_list_id")}
                  variant="warning"
                />
              )}
              <Input
                {...register("access_token")}
                label={fieldLabels?.access_token || t("access_token")}
                data-cy="input-access_token"
                placeholder={
                  fieldLabels?.placeholder_access_token ||
                  t("access_token_placeholder")
                }
                helperText={errors.access_token?.message}
                subLabel={
                  integrationKey === "tauk"
                    ? t("integration:tauk_description_list_id")
                    : undefined
                }
                onChange={(e) => {
                  setValue("access_token", e.target.value);
                  clearErrors("access_token");
                }}
              />
            </motion.div>
          )}

          {fields.includes("metadata") && (
            <motion.div variants={itemVariants}>
              {renderMetadataField()}
            </motion.div>
          )}

          {fields.includes("secret_token") && (
            <motion.div variants={itemVariants}>
              <Input
                {...register("secret_token")}
                label={fieldLabels?.secret_token || t("secret_token")}
                data-cy="input-secret_token"
                placeholder={
                  fieldLabels?.placeholder_secret_token ||
                  t("secret_token_placeholder")
                }
                helperText={errors.secret_token?.message}
                onChange={(e) => {
                  setValue("secret_token", e.target.value);
                  clearErrors("secret_token");
                }}
              />
            </motion.div>
          )}

          {fields.includes("postback_url") && (
            <motion.div variants={itemVariants}>
              <Input
                {...register("postback_url")}
                label={fieldLabels?.postback_url || t("postback_url")}
                data-cy="input-postback_url"
                placeholder={
                  fieldLabels?.placeholder_postback_url ||
                  t("postback_url_placeholder")
                }
                helperText={errors.postback_url?.message}
                onChange={(e) => {
                  setValue("postback_url", e.target.value);
                  clearErrors("postback_url");
                }}
              />
            </motion.div>
          )}

          {!fixedValues?.products && (
            <motion.div variants={itemVariants}>
              <MultiComboBox
                fetchOptions={fetchOptions}
                values={watch("products")}
                onChange={(value) => {
                  setValue("products", value);
                  clearErrors("products");
                }}
                label={fieldLabels?.products || t("products")}
                placeholder={
                  fieldLabels?.placeholder_products || t("products_placeholder")
                }
                queryKey="products"
                newParams={wmsProductFetchParams}
                helperText={errors.products?.message}
                cypressName="select-products"
              />
            </motion.div>
          )}

          {!fixedValues?.active_statuses && (
            <motion.div
              variants={itemVariants}
              className="border-y py-6 space-y-4 my-4 border-y-colorBorder"
            >
              <h2 className="font-medium">
                {fieldLabels?.payment_status || t("payment_status")}
              </h2>
              <StatusSelector
                options={(fixedOptions?.active_statuses || paymentStatus).map(status => ({ value: status, label: t(status) }))}
                selectedOptions={defaultValues.active_statuses ?? []}
                onChange={(statuses) => {
                  setValue('active_statuses', statuses);
                  clearErrors('active_statuses');
                }}
                variant="default"
              />
              {errors?.active_statuses && (
                <motion.p
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-sm text-error"
                >
                  {errors?.active_statuses?.message}
                </motion.p>
              )}

              {/* Sub-opções do initiate_checkout */}
              {defaultValues.active_statuses?.includes('initiate_checkout') && (
                <motion.div
                  initial={{ opacity: 0, height: 0 }}
                  animate={{ opacity: 1, height: 'auto' }}
                  exit={{ opacity: 0, height: 0 }}
                  transition={{ duration: 0.25 }}
                  className="mt-3 space-y-2.5"
                >
                  <Label className="text-xs font-medium text-zinc-400">
                    {t("integration:ic_trigger_label")}
                  </Label>
                  <div className="grid gap-1.5 grid-cols-1">
                    {([
                      { value: 'checkout_open', label: t("integration:ic_trigger_checkout_open") },
                      { value: 'email_fill', label: t("integration:ic_trigger_email_fill") },
                      { value: 'all_contact_data', label: t("integration:ic_trigger_all_contact_data") },
                    ] as const).map((option) => {
                      const currentTrigger = (typeof defaultValues.metadata === 'object' && defaultValues.metadata !== null && !Array.isArray(defaultValues.metadata))
                        ? (defaultValues.metadata as Record<string, unknown>).initiate_checkout_trigger as string ?? 'email_fill'
                        : 'email_fill';
                      const isSelected = currentTrigger === option.value;
                      return (
                        <button
                          key={option.value}
                          type="button"
                          onClick={() => {
                            const currentMeta = (typeof defaultValues.metadata === 'object' && defaultValues.metadata !== null && !Array.isArray(defaultValues.metadata))
                              ? { ...(defaultValues.metadata as Record<string, unknown>) }
                              : {};
                            setValue('metadata', { ...currentMeta, initiate_checkout_trigger: option.value });
                          }}
                          className={cn(
                            'flex items-center gap-2.5 rounded-lg text-left transition-all duration-150 cursor-pointer group/item px-3 py-2.5 min-h-[44px]',
                            isSelected
                              ? 'bg-primary/8 ring-1 ring-primary/30'
                              : 'bg-zinc-900/40 hover:bg-zinc-800/50 ring-1 ring-zinc-800/60'
                          )}
                        >
                          <div
                            className={cn(
                              'w-4 h-4 rounded-full flex items-center justify-center shrink-0 transition-all duration-150',
                              isSelected
                                ? 'border-[5px] border-primary'
                                : 'border border-zinc-600 group-hover/item:border-zinc-400'
                            )}
                          />
                          <span
                            className={cn(
                              'text-xs leading-tight transition-colors duration-150',
                              isSelected
                                ? 'text-colorText font-medium'
                                : 'text-zinc-400 group-hover/item:text-zinc-300'
                            )}
                          >
                            {option.label}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </motion.div>
              )}
            </motion.div>
          )}

          {!fixedValues?.payment_methods && (
            <motion.div variants={itemVariants} className="space-y-4">
              <h2 className="font-medium">
                {fieldLabels?.payment_type || t("payment_type")}
              </h2>
              <PaymentMethodSelector
                showHeader={false}
                availableMethods={resolvedAvailableMethods}
                selectedMethods={defaultValues.payment_methods}
                onChange={(methods) => {
                  setValue("payment_methods", methods);
                  clearErrors("payment_methods");
                }}
              />
              {errors?.payment_methods && (
                <motion.p
                  initial={{ opacity: 0, y: -5 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="text-sm text-error"
                >
                  {errors?.payment_methods?.message}
                </motion.p>
              )}
            </motion.div>
          )}
        </motion.section>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4, duration: 0.3 }}
        >
          <SheetFooter className="flex border-t border-t-colorBorder p-4 items-end mt-4 justify-end max-md:pb-24 flex-wrap gap-4 max-md:flex-col">
            {(hasCoursesError || hasListsError) && (
              <Notice
                message={
                  (coursesError || (listsError as any))?.response?.data
                    ?.message || t("data_listing_error")
                }
                variant="error"
              />
            )}
            <div className="flex gap-2 justify-end w-full sm:w-auto">
              {hasTestSupport && (
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <motion.div
                        whileHover={canTestIntegration ? { scale: 1.02 } : {}}
                        whileTap={canTestIntegration ? { scale: 0.98 } : {}}
                      >
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setIsTestModalOpen(true)}
                          disabled={!canTestIntegration}
                          className={cn(
                            "gap-2 transition-all duration-300",
                            canTestIntegration && "border-primary/50 hover:border-primary hover:bg-primary/10"
                          )}
                        >
                          <Zap className={cn(
                            "w-4 h-4 transition-colors",
                            canTestIntegration && "text-primary"
                          )} />
                          {t("integration:test_webhook.test_button")}
                        </Button>
                      </motion.div>
                    </TooltipTrigger>
                    {!canTestIntegration && missingFieldsForTest.length > 0 && (
                      <TooltipContent side="top" className="max-w-[250px]">
                        <p className="font-medium mb-1">
                          {t("integration:test_webhook.fill_required_fields")}
                        </p>
                        <ul className="text-xs space-y-0.5 text-muted-foreground">
                          {missingFieldsForTest.map((field) => (
                            <li key={field} className="flex items-center gap-1">
                              <span className="w-1 h-1 rounded-full bg-primary" />
                              {t(`integration:test_webhook.field_${field}`)}
                            </li>
                          ))}
                        </ul>
                      </TooltipContent>
                    )}
                  </Tooltip>
                </TooltipProvider>
              )}
              <Button
                variant="ghost"
                onClick={() => closeModal(modalKey)}
                type="button"
                data-cy="cancel-integration"
              >
                {t("cancel")}
              </Button>
              <motion.div
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
              >
                <Button
                  type="submit"
                  disabled={isPending}
                  data-cy="submit-integration"
                  className="min-w-[120px]"
                >
                  {isPending ? (
                    <span className="flex items-center gap-2">
                      <Loader2 className="animate-spin" />
                      {t("saving")}
                    </span>
                  ) : (
                    t("save_integration")
                  )}
                </Button>
              </motion.div>
            </div>
          </SheetFooter>
        </motion.div>
      </form>

      {hasTestSupport && (
        <IntegrationTestModal
          isOpen={isTestModalOpen}
          onClose={() => setIsTestModalOpen(false)}
          integrationKey={integrationKey}
          postbackUrl={watch("postback_url") || ""}
          products={watch("products") || []}
          availableStatuses={availableStatusesForTest}
          availablePaymentMethods={availablePaymentMethodsForTest}
        />
      )}
    </section>
  );
}
